/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {FrustumVolume} from "App/utils/view_frustum";
import {mat4, vec3} from "gl-matrix";
import PatchManager from "App/utils/globe/chunk_lod/patch_manager";
import ModelManager from "App/utils/globe/chunk_lod/model_manager";
import PatchAddress from "App/utils/globe/chunk_lod/patch_address";
import ModelAddress from "App/utils/globe/chunk_lod/model_address";
import IGlobeWorker from "App/workers/globe_interface";

const ctx: Worker = self as any;

export default
class GlobeWorker implements IGlobeWorker {
    public static EnableDebug: boolean = false;
    private _origin: string;
    private _baseUrl: string;
    private _isMobile: boolean;
    private _currentView: any;
    private _currentViewChanged: boolean;
    private _messagesToSendBuffer: { dirty: boolean; messages: any[] };
    private _updatesLimited: boolean;
    private _zeropointTranslationMatrix: mat4;
    private _patches: PatchManager[];
    private _models: ModelManager[];

    private _nonWorkerCallback: (message: any) => void = null;

    constructor() {
        setInterval(this._updateParts.bind(this), 150);

        this._origin = null;
        this._baseUrl = null;
        this._patches = [];
        this._models = [];
        this._isMobile = false;

        // actually we do not have one tile covering whole globe but instead we have the swiss patch
        // each with size of 1km x 1km so we crate a chunk tree for each of these patches
        this._currentView = null;
        this._currentViewChanged = false;

        this._messagesToSendBuffer = {
            messages: [],
            dirty: false
        };

        this._updatesLimited = true;
    }

    public getDebugEnabled(): boolean {
        return GlobeWorker.EnableDebug;
    }

    public schedulePostMessage(aMessage: any) {
        this._messagesToSendBuffer.messages.push(aMessage);
        this._messagesToSendBuffer.dirty = true;
    }

    private _recomputeFrustumVolume(aVPMatrix: mat4) {
        this._currentView.viewFrustum = new FrustumVolume(aVPMatrix);
    }

    public viewpointChanged(aMessage: any) {
        this._currentViewChanged = true;
        this._currentView = aMessage.views[0];

        this._recomputeFrustumVolume(aMessage.views[0].vpm);

        //this.downloadManager.scheduleRecomputeDownloadPriority(aMessage.views[0]);
        //this.texturesManager.scheduleRecomputeTexturesResolution(aMessage.views[0]);
    }

    private _updateParts() {
        if (!this._currentView) {
            return;
        }

        if (this._currentViewChanged) {
            this._currentViewChanged = false;

            let cameraPosition = this._currentView.eye;

            // sort patches according to distance from camera
            this._patches.sort(function (tileA, tileB) {
                return vec3.distance(tileA.getCenter(), cameraPosition) - vec3.distance(tileB.getCenter(), cameraPosition);
            });

            // sort models according to distance from camera
            this._models.sort(function (modelA, modelB) {
                return vec3.distance(modelA.getCenter(), cameraPosition) - vec3.distance(modelB.getCenter(), cameraPosition);
            });

            // inject current view to patches/models
            for (let i = 0; i < this._patches.length; i++) {
                this._patches[i].setView(this._currentView);
            }

            for (let i = 0; i < this._models.length; i++) {
                this._models[i].setView(this._currentView);
            }
        }

        for (let i = 0; i < this._patches.length; i++) {
            this._patches[i].update();
        }

        for (let i = 0; i < this._models.length; i++) {
            this._models[i].update();
        }

        if (this._messagesToSendBuffer.dirty) {

            this._nonWorkerCallback({ data: {
                type: "messagesBuffer",
                messages: this._messagesToSendBuffer.messages
            }});

            this._messagesToSendBuffer.messages = [];
            this._messagesToSendBuffer.dirty = false;
        }
    }

    public getOrigin(): string {
        return this._origin;
    }

    public getBase(): string {
        return this._baseUrl;
    }

    public getIsMobile(): boolean {
        return this._isMobile;
    }

    public enableDebug(aMessage: {enableDebug: boolean}) {
        GlobeWorker.EnableDebug = aMessage.enableDebug;
    }

    public updatesLimited(aMessage: {limited: boolean}) {
        this._updatesLimited = aMessage.limited;
    }

    public getUpdatesLimited(): boolean {
        return this._updatesLimited;
    }

    public startRendering(aMessage: any) {
        this._origin = aMessage.origin;
        this._baseUrl = aMessage.base;
        this._isMobile = aMessage.isMobile;
        this._zeropointTranslationMatrix = aMessage.zeropointTranslationMatrix;

        PatchAddress.startingOffset.x = this._zeropointTranslationMatrix[12];
        PatchAddress.startingOffset.y = this._zeropointTranslationMatrix[14];

        // create root for all patches in input range
        for (let i = 0; i < aMessage.patches.length; i++) {
            this._patches.push(
                new PatchManager(
                    this,
                    new PatchAddress({
                        base: this.getBase(),
                        origin: this.getOrigin(),
                        patchInfo: aMessage.patches[i],
                        level: 0,
                        layer: 0,
                        row: 0,
                        col: 0
                    })
                )
            );
        }

        for (let i = 0; i < aMessage.models.length; i++) {
            this._models.push(
                new ModelManager(
                    this,
                    new ModelAddress({
                        base: this.getBase(),
                        origin: this.getOrigin(),
                        patchInfo: aMessage.models[i],
                        level: 0,
                        layer: 0,
                        row: 0,
                        col: 0
                    })
                )
            );
        }

        // notify renderer that scene is "loaded", this should happen when something is rendered already instead
        this._nonWorkerCallback({ data: {
            type: "sceneLoaded"
        }});
    }

    public settledOnGPU(aMessage: any) {

        for (let i = 0; i < this._patches.length; i++) {
            let patch = this._patches[i];
            if (patch.getName() === aMessage.patchInfo.name) {
                patch.settledOnGPU(aMessage);
            }
        }

        for (let i = 0; i < this._models.length; i++) {
            let model = this._models[i];
            if (model.getName() === aMessage.patchInfo.name) {
                model.settledOnGPU(aMessage);
            }
        }
    }

    public acceptMessage(e) {
        for (let i = 0; i < e.data.messages.length; i++) {
            let message = e.data.messages[i];
            switch (message.type) {
                case 'viewpointChanged':
                    this.viewpointChanged(message);
                    break;
                case 'startRendering':
                    this.startRendering(message);
                    break;
                case 'updatesLimited':
                    this.updatesLimited(message);
                    break;
                case 'enableDebug':
                    this.enableDebug(message);
                    break;
                case 'settledOnGPU':
                    this.settledOnGPU(message);
                    break;
            }
        }
    }

    public postMessage(e: any) {
        setTimeout(this.acceptMessage.bind(this, {data: e}), 10);
    }

    /**
     * this is for when we run the worker as not worker
     */
    public addEventListener(e, callback) {
        this._nonWorkerCallback = callback;
    }
}

/*
let globeWorker = new GlobeWorker();

ctx.addEventListener('message', function(e) {
    globeWorker.acceptMessage(e);
}, false);
*/